/**
 * Instance Capture Hook
 *
 * Single capture loop that handles screenshot capture for instance cards.
 *
 * Key design decisions:
 * - Single capture loop, not per-instance - prevents overwhelming the system
 * - Captures ONE instance at a time via canvas idle tasks
 * - Captures instances in order (as they appear in the list)
 * - Memory limit: 500MB blob size, waits for room before capturing more
 * - Failure tracking: max 3 retries per instance
 * - Upload runs in background (fire-and-forget with cleanup)
 *
 * ## Capture Loop Pseudocode
 *
 * while mounted:
 *   wait for memory room
 *   find next instance to capture (first uncaptured with contentWindow)
 *   if none:
 *     wait for work signal (new instances or contentWindows)
 *     continue
 *   capture screenshot via scheduleCanvasIdleTask
 *   if success:
 *     store dataUrl for immediate display
 *     update contentWindowData (dimensions, captureTs)
 *     upload in background, clear dataUrl when done
 *   if failed:
 *     increment failure count
 *   if not_ready:
 *     mark as attempted (skip until hotReloadTs changes)
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSession } from "@clerk/clerk-react";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import {
  getUnexpiredToken,
  useAutoUpdateRef,
  useCanvasId,
  useSelectedPageId,
} from "@/utils/utils";
import {
  makeResettableSignal,
  scheduleIdleTask,
} from "@/utils/schedulingUtils";
import { captureContentWindowInstanceData } from "./captureUtils";
import {
  computeComponentFileKey,
  getInstanceUniqueKey,
  useInstancesStore,
  type Instance,
} from "./instancesStore";
import { uploadInstanceScreenshot } from "./utils";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const BLOB_SIZE_LIMIT_BYTES = 1 * 1024 * 1024 * 1024; // 1GB
const MAX_CAPTURE_FAILURES = 3;
const SCREENSHOT_STALE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

export interface UseCaptureInstancesResult {
  /**
   * Manually trigger capture for a specific instance (e.g., on click).
   */
  captureInstance: (instance: Instance) => Promise<void>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HOOK
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Capture hook that runs a single loop to capture instance screenshots.
 *
 * @param component - The component definition, or null if none selected
 * @param instancesToCapture - Instances that need capture (from current view)
 * @returns Object with captureInstance function for manual capture
 */
export function useCaptureInstances(
  component: JsxElement | null,
  instancesToCapture: Instance[],
): UseCaptureInstancesResult {
  const canvasId = useCanvasId();
  const { session } = useSession();
  const isMountedRef = useRef(true);

  // Component file key for store updates
  const componentFileKey = useMemo(
    () =>
      canvasId && component
        ? computeComponentFileKey(
            component.filename || "",
            component.componentName || "",
            canvasId,
          )
        : "",
    [canvasId, component?.filename, component?.componentName],
  );

  // Signal triggered when there are new instances to capture
  const hasWorkSignal = useMemo(() => makeResettableSignal(), []);
  // Signal triggered when blob memory drops below limit
  const hasMemoryRoomSignal = useMemo(() => makeResettableSignal(true), []);

  // Capture state (refs to avoid re-renders)
  const currentBlobSizeRef = useRef(0);
  const captureFailuresRef = useRef<Map<string, number>>(new Map());
  const processingKeysRef = useRef<Set<string>>(new Set());
  // Track instances we've successfully captured this session (prevents infinite loops
  // if store update fails silently due to key mismatch or timing issues)
  const capturedThisSessionRef = useRef<Map<string, number>>(new Map());
  // AbortController for canceling in-flight operations on unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  // Latest props ref for async access (useAutoUpdateRef updates synchronously during render)
  const propsRef = useAutoUpdateRef({
    canvasId,
    componentFileKey,
    session,
    componentFilename: component?.filename || "",
    componentName: component?.componentName || "",
  });

  // Track instance keys to detect new arrivals
  const prevInstanceKeysRef = useRef<Set<string>>(new Set());

  // Signal when new instances arrive
  useEffect(() => {
    if (!canvasId || !componentFileKey) return;

    const currentKeys = new Set(
      instancesToCapture.map((i) => getInstanceUniqueKey(i, canvasId)),
    );
    const prevKeys = prevInstanceKeysRef.current;

    // Check if there are any new instances
    let hasNewInstances = false;
    for (const key of currentKeys) {
      if (!prevKeys.has(key)) {
        hasNewInstances = true;
        break;
      }
    }

    prevInstanceKeysRef.current = currentKeys;

    if (hasNewInstances) {
      hasWorkSignal.trigger();
    }
  }, [canvasId, componentFileKey, instancesToCapture, hasWorkSignal]);

  // Signal when contentWindows become available for storyboards our instances are on.
  // This directly tracks the key condition in findNextInstanceToCapture:
  // whether storyboardIdToContentWindow[instance.storyboardId] exists.
  const storyboardIdToContentWindow = useIframePoolStore(
    (state) => state.storyboardIdToContentWindow,
  );

  // Signal when contentWindows become available for instances we need to capture
  useEffect(() => {
    if (
      instancesToCapture.some(
        (i) => i.storyboardId in storyboardIdToContentWindow,
      )
    ) {
      hasWorkSignal.trigger();
    }
  }, [instancesToCapture, storyboardIdToContentWindow, hasWorkSignal]);

  // ═══════════════════════════════════════════════════════════════════════════
  // CAPTURE LOOP
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    isMountedRef.current = true;

    // Create new AbortController for this effect instance
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    /**
     * Checks if an instance has a fresh screenshot that doesn't need recapture.
     * A screenshot needs recapture if:
     * - It doesn't exist
     * - It's stale (older than 7 days)
     * - The component has been hot-reloaded since capture
     */
    const hasFreshScreenshot = (
      instance: Instance,
      instanceKey: string,
    ): boolean => {
      const hasScreenshot = !!instance.contentWindowData?.screenshotUrl;
      if (!hasScreenshot) return false;

      const captureTs = instance.contentWindowData?.captureHotReloadTs ?? 0;
      const hotReloadTs = instance.hotReloadTs ?? 0;
      const isStale =
        captureTs > 0 && Date.now() - captureTs > SCREENSHOT_STALE_MS;

      if (isStale) return false;

      // Fresh if captured at or after current hotReloadTs
      if (captureTs >= hotReloadTs) return true;

      // Fresh if captured this session at or after current hotReloadTs
      const sessionCaptureTs = capturedThisSessionRef.current.get(instanceKey);
      if (sessionCaptureTs && sessionCaptureTs >= hotReloadTs) return true;

      // Fresh if this is a page reload (hotReloadTs unchanged from initial)
      const initialTs = useInstancesStore
        .getState()
        .getInitialHotReloadTs(instanceKey);
      if (initialTs && hotReloadTs <= initialTs) return true;

      return false;
    };

    /**
     * Finds the next instance to capture (first uncaptured with available contentWindow).
     */
    const findNextInstanceToCapture = (): Instance | null => {
      const { canvasId, componentFileKey } = propsRef.current;
      if (!canvasId) return null;

      const freshInstances = useInstancesStore
        .getState()
        .getInstancesForComponent(canvasId, componentFileKey);

      for (const instance of freshInstances) {
        const key = getInstanceUniqueKey(instance, canvasId);

        // Skip: already being processed
        if (processingKeysRef.current.has(key)) continue;

        // Skip: no contentWindow (iframe not loaded yet)
        const contentWindow =
          useIframePoolStore.getState().storyboardIdToContentWindow[
            instance.storyboardId
          ];
        if (!contentWindow) continue;

        // Skip: already has fresh screenshot
        if (hasFreshScreenshot(instance, key)) continue;

        // Skip: exceeded failure limit
        const failures = captureFailuresRef.current.get(key) ?? 0;
        if (failures >= MAX_CAPTURE_FAILURES) continue;

        return instance;
      }

      return null;
    };

    /**
     * Captures a single instance.
     */
    const captureOne = async (instance: Instance) => {
      const { canvasId, componentFileKey, componentFilename, componentName } =
        propsRef.current;

      if (!canvasId) return;

      const instanceKey = getInstanceUniqueKey(instance, canvasId);
      processingKeysRef.current.add(instanceKey);

      const contentWindow =
        useIframePoolStore.getState().storyboardIdToContentWindow[
          instance.storyboardId
        ];
      if (!contentWindow) {
        processingKeysRef.current.delete(instanceKey);
        return;
      }

      const navNode = useInstancesStore.getState().getNavNode(instanceKey);

      const outcome = await captureContentWindowInstanceData(
        instance.domUniquePath,
        instance.hotReloadTs,
        contentWindow,
        canvasId,
        navNode,
      );

      if (outcome.status === "success") {
        const { blob, dataUrl, width, height, captureHotReloadTs, dragData } =
          outcome.data;
        const blobSize = blob.size;

        currentBlobSizeRef.current += blobSize;
        captureFailuresRef.current.delete(instanceKey);

        // Track that we captured this instance (prevents infinite loops if store update fails)
        if (captureHotReloadTs !== undefined) {
          capturedThisSessionRef.current.set(instanceKey, captureHotReloadTs);
        }

        const store = useInstancesStore.getState();

        // Store dataUrl for immediate display
        store.setScreenshotDataUrl(instanceKey, dataUrl);

        // Update instance with dimensions
        store.updateInstanceContentWindowData(
          canvasId,
          componentFileKey,
          instance.storyboardId,
          instanceKey,
          { width, height, captureHotReloadTs },
        );

        // Update drag data if captured
        if (dragData && Object.keys(dragData).length > 0) {
          store.updateInstanceDragData(
            canvasId,
            componentFileKey,
            instance.storyboardId,
            instanceKey,
            dragData,
          );
        }

        // Upload in background
        uploadInBackground(
          blob,
          blobSize,
          instanceKey,
          instance,
          canvasId,
          componentFileKey,
          componentFilename,
          componentName,
        );
      } else if (outcome.status === "failed") {
        const failures = (captureFailuresRef.current.get(instanceKey) ?? 0) + 1;
        captureFailuresRef.current.set(instanceKey, failures);
        processingKeysRef.current.delete(instanceKey);
      } else {
        // "not_ready" - mark as attempted so we don't retry until hotReloadTs changes
        capturedThisSessionRef.current.set(
          instanceKey,
          instance.hotReloadTs ?? 0,
        );
        processingKeysRef.current.delete(instanceKey);
      }
    };

    /**
     * Uploads screenshot to GCS in background, then cleans up.
     */
    const uploadInBackground = async (
      blob: Blob,
      blobSize: number,
      instanceKey: string,
      instance: Instance,
      canvasId: string,
      componentFileKey: string,
      componentFilename: string,
      componentName: string,
    ) => {
      let uploadSucceeded = false;
      try {
        const currentSession = propsRef.current.session;
        if (currentSession) {
          const jwt = await getUnexpiredToken(currentSession);
          if (jwt) {
            const url = await uploadInstanceScreenshot({
              blob,
              canvasId,
              storyboardId: instance.storyboardId,
              componentFilename,
              componentName,
              instanceCodebaseId: instance.instanceCodebaseId,
              supabaseJWT: jwt,
            });

            if (url) {
              uploadSucceeded = true;
              useInstancesStore
                .getState()
                .updateInstanceContentWindowData(
                  canvasId,
                  componentFileKey,
                  instance.storyboardId,
                  instanceKey,
                  { screenshotUrl: url },
                );
            }
          }
        }
      } finally {
        // Only clear dataUrl if upload succeeded (we have permanent URL now)
        if (uploadSucceeded) {
          useInstancesStore.getState().clearScreenshotDataUrl(instanceKey);
        }

        processingKeysRef.current.delete(instanceKey);
        currentBlobSizeRef.current -= blobSize;

        if (currentBlobSizeRef.current < BLOB_SIZE_LIMIT_BYTES) {
          hasMemoryRoomSignal.trigger();
        }
      }
    };

    /**
     * Main capture loop. Checks abort signal to exit cleanly on unmount.
     */
    const runCaptureLoop = async () => {
      while (isMountedRef.current && !signal.aborted) {
        // Wait for memory room if over limit
        while (
          currentBlobSizeRef.current >= BLOB_SIZE_LIMIT_BYTES &&
          isMountedRef.current &&
          !signal.aborted
        ) {
          hasMemoryRoomSignal.reset();
          if (currentBlobSizeRef.current >= BLOB_SIZE_LIMIT_BYTES) {
            await hasMemoryRoomSignal.wait();
          }
        }
        if (!isMountedRef.current || signal.aborted) break;

        // Find next instance to capture
        hasWorkSignal.reset();
        const instance = findNextInstanceToCapture();
        if (!instance) {
          // No work - wait for signal (already reset above)
          await hasWorkSignal.wait();
          continue;
        }

        // Check abort before starting capture
        if (signal.aborted) break;

        // Capture the instance
        await scheduleIdleTask(() => captureOne(instance));
      }
    };

    runCaptureLoop();

    return () => {
      isMountedRef.current = false;
      abortController.abort();
      hasWorkSignal.trigger();
      hasMemoryRoomSignal.trigger();
    };
  }, [hasWorkSignal, hasMemoryRoomSignal]);

  // ═══════════════════════════════════════════════════════════════════════════
  // MANUAL CAPTURE (for click)
  // ═══════════════════════════════════════════════════════════════════════════

  const captureInstance = useCallback(
    async (instance: Instance) => {
      if (!canvasId || !componentFileKey) return;

      const contentWindow =
        useIframePoolStore.getState().storyboardIdToContentWindow[
          instance.storyboardId
        ];
      if (!contentWindow) return;

      const instanceKey = getInstanceUniqueKey(instance, canvasId!);
      const store = useInstancesStore.getState();
      const navNode = store.getNavNode(instanceKey);

      const outcome = await captureContentWindowInstanceData(
        instance.domUniquePath,
        instance.hotReloadTs,
        contentWindow,
        canvasId,
        navNode,
      );

      if (outcome.status === "success") {
        const result = outcome.data;
        store.setScreenshotDataUrl(instanceKey, result.dataUrl);
        store.updateInstanceContentWindowData(
          canvasId,
          componentFileKey,
          instance.storyboardId,
          instanceKey,
          {
            width: result.width,
            height: result.height,
            captureHotReloadTs: result.captureHotReloadTs,
          },
        );

        if (result.dragData && Object.keys(result.dragData).length > 0) {
          store.updateInstanceDragData(
            canvasId,
            componentFileKey,
            instance.storyboardId,
            instanceKey,
            result.dragData,
          );
        }
      }
    },
    [canvasId, componentFileKey],
  );

  return { captureInstance };
}
